import type { StateCreator } from 'zustand';

import type ProcessStatus from '../../interfaces/ProcessStatus';

export interface FileDownloadSlice {
  fileDownloadProcessStatuses: ProcessStatus[];
  activeFileDownloadProcessIds: string[];
  isFileDownloadLoading: boolean;
  setFileDownloadProcessStatuses: (
    statuses: ProcessStatus[] | ((previousStatuses: ProcessStatus[]) => ProcessStatus[]),
  ) => void;
  upsertFileDownloadProcess: (process: ProcessStatus) => void;
  replaceFileDownloadProcessId: (
    previousProcessId: string,
    nextProcessId: string,
    updates?: Partial<ProcessStatus>,
  ) => void;
  setActiveFileDownloadProcessIds: (
    processIds: string[] | ((previousIds: string[]) => string[]),
  ) => void;
  setIsFileDownloadLoading: (isLoading: boolean) => void;
  resetFileDownloadState: () => void;
}

const initialFileDownloadState = {
  fileDownloadProcessStatuses: [] as ProcessStatus[],
  activeFileDownloadProcessIds: [] as string[],
  isFileDownloadLoading: false,
};

export const createFileDownloadSlice: StateCreator<FileDownloadSlice, [], [], FileDownloadSlice> = (
  set,
) => ({
  ...initialFileDownloadState,

  setFileDownloadProcessStatuses: (statuses) => {
    set((state) => ({
      fileDownloadProcessStatuses:
        typeof statuses === 'function' ? statuses(state.fileDownloadProcessStatuses) : statuses,
    }));
  },

  upsertFileDownloadProcess: (process) => {
    set((state) => {
      const index = state.fileDownloadProcessStatuses.findIndex(
        (status) => status.processId === process.processId,
      );
      if (index >= 0) {
        const nextStatuses = [...state.fileDownloadProcessStatuses];
        nextStatuses[index] = process;
        return { fileDownloadProcessStatuses: nextStatuses };
      }
      return {
        fileDownloadProcessStatuses: [...state.fileDownloadProcessStatuses, process],
      };
    });
  },

  replaceFileDownloadProcessId: (previousProcessId, nextProcessId, updates = {}) => {
    set((state) => ({
      fileDownloadProcessStatuses: state.fileDownloadProcessStatuses.map((status) =>
        status.processId === previousProcessId
          ? { ...status, ...updates, processId: nextProcessId }
          : status,
      ),
    }));
  },

  setActiveFileDownloadProcessIds: (processIds) => {
    set((state) => ({
      activeFileDownloadProcessIds:
        typeof processIds === 'function'
          ? processIds(state.activeFileDownloadProcessIds)
          : processIds,
    }));
  },

  setIsFileDownloadLoading: (isLoading) => set({ isFileDownloadLoading: isLoading }),

  resetFileDownloadState: () => set({ ...initialFileDownloadState }),
});
